import type { ConsoleClient } from "@colossus/api-client";
import { browser } from "$app/env";
import { consoleRead, queryClient } from "../query.ts";
import { perReader } from "../reader.ts";
import { showingDemo } from "../demo/live.ts";
import type { ConsoleOk } from "../query.ts";

type Access = "active" | "suspended";

export interface SessionUser {
  readonly accountId: string;
  readonly discordUserId: string;
  readonly username: string;
  readonly avatarUrl: string;
  readonly globalName: string | null;
  readonly bannerUrl: string | null;
  readonly accentColor: number | null;
  readonly access: Access;
  readonly staff: boolean;
  readonly lastTenantId: string | null;
}

const session = perReader(
  (): {
    cached: SessionUser | null | undefined;
    reading: Promise<SessionUser | null> | undefined;
  } => ({ cached: undefined, reading: undefined }),
);

const SESSION_HINT_KEY = "colossus.session";

export const rememberSession = (signedIn: boolean): void => {
  if (!browser) return;
  if (signedIn) localStorage.setItem(SESSION_HINT_KEY, "1");
  else localStorage.removeItem(SESSION_HINT_KEY);
};

export const sessionHinted = (): boolean =>
  browser && localStorage.getItem(SESSION_HINT_KEY) === "1";

const userOf = (me: ConsoleOk<"/api/auth/me">): SessionUser => ({
  accountId: me.accountId,
  discordUserId: me.discordUserId,
  username: me.username,
  avatarUrl: me.avatarUrl,
  globalName: me.globalName ?? null,
  bannerUrl: me.bannerUrl ?? null,
  accentColor: me.accentColor ?? null,
  access: me.access,
  staff: me.staff,
  lastTenantId: me.lastTenantId,
});

const readSession = async (client: ConsoleClient): Promise<SessionUser | null | undefined> => {
  const booted = queryClient().getQueryData(consoleRead("/api/auth/me").queryKey);
  if (booted !== undefined) return userOf(booted);
  try {
    const result = await client.call("get", "/api/auth/me");
    if (!result.ok) return result.status === 401 ? null : undefined;
    return userOf(result.data);
  } catch {
    return undefined;
  }
};

export const sessionUser = async (client: ConsoleClient): Promise<SessionUser | null> => {
  const held = session();
  if (held.cached !== undefined) return held.cached;
  held.reading ??= readSession(client).then((user) => {
    held.cached = user;
    held.reading = undefined;
    const signedIn = user ?? null;
    if (!showingDemo()) rememberSession(signedIn !== null);
    return signedIn;
  });
  return held.reading;
};

export const sessionIn = (data: Readonly<Record<string, unknown>>): SessionUser | null => {
  const me = data["me"];
  return typeof me === "object" && me !== null && "discordUserId" in me
    ? (me as SessionUser)
    : null;
};

export const signedInHere = (): boolean => {
  const { cached } = session();
  return cached !== undefined && cached !== null;
};

const forgetSessionUser = (): void => {
  session().cached = undefined;
};

export const leaveSignedOut = (): void => {
  forgetSessionUser();
  rememberSession(false);
  globalThis.location.assign("/");
};

export const signOut = async (client: ConsoleClient): Promise<void> => {
  await client.call("post", "/api/auth/logout");
  leaveSignedOut();
};
